import React, { useState } from 'react';
import { 
    Box, Typography, Container, Tabs, Tab, 
    Accordion, AccordionSummary, AccordionDetails, 
    Divider, Chip, Paper, useTheme, Tooltip, IconButton
} from '@mui/material';
import { 
    ExpandMore as ExpandMoreIcon, 
    Code as CodeIcon, 
    Public as PublicIcon, 
    Settings as SettingsIcon,
    BarChart as BarChartIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

// --- MOCK API DATA (Functional Content) ---
const apiData = [
    {
        category: 'User Management API',
        icon: PublicIcon,
        endpoints: [
            { method: 'GET', path: '/api/v1/users', description: 'Retrieve a list of all users.', response: { status: 200, body: [{ id: 1, name: "Admin User" }, { id: 2, name: "Jane Doe" }] } },
            { method: 'POST', path: '/api/v1/users', description: 'Create a new user account.', response: { status: 201, body: { id: 3, status: 'created', link: '/api/v1/users/3' } } },
            { method: 'DELETE', path: '/api/v1/users/{id}', description: 'Delete a specific user account.', response: { status: 204, body: null } },
        ]
    },
    {
        category: 'System Configuration API',
        icon: SettingsIcon,
        endpoints: [
            { method: 'GET', path: '/api/v1/settings/health', description: 'Check the current system health status.', response: { status: 200, body: { db: 'UP', cache: 'UP', status: 'OK', latency_ms: 50 } } },
            { method: 'PUT', path: '/api/v1/settings/maintenance', description: 'Toggle maintenance mode on or off.', response: { status: 200, body: { mode: 'ON', until: '2026-01-01' } } },
        ]
    },
    {
        category: 'Reporting & Analytics API',
        icon: BarChartIcon,
        endpoints: [
            { method: 'GET', path: '/api/v1/reports/audit', description: 'Fetch recent audit logs.', response: { status: 200, body: [{ timestamp: '2025-10-01T10:00:00Z', user: 'admin', action: 'system_update' }] } },
            { method: 'GET', path: '/api/v1/reports/usage', description: 'Get monthly system usage statistics.', response: { status: 200, body: { total_requests: 150000, active_users: 500 } } },
        ]
    },
];

// Helper function for Method Chip colors
const getMethodColor = (method) => {
    switch (method) {
        case 'GET': return { label: 'GET', color: 'success', sx: { backgroundColor: '#4CAF50', color: 'white' } };
        case 'POST': return { label: 'POST', color: 'primary', sx: { backgroundColor: '#2196F3', color: 'white' } };
        case 'PUT': return { label: 'PUT', color: 'warning', sx: { backgroundColor: '#FF9800', color: 'white' } };
        case 'DELETE': return { label: 'DELETE', color: 'error', sx: { backgroundColor: '#F44336', color: 'white' } };
        default: return { label: method, color: 'default', sx: { backgroundColor: '#607D8B', color: 'white' } };
    }
};

// --- MAIN COMPONENT ---
const APIDocumentation = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const [copied, setCopied] = useState(false);
    const theme = useTheme();

    const handleChange = (event, newValue) => {
        setCurrentTab(newValue);
    };
    
    // Pro-level Copy-to-Clipboard Functionality
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset state after 2 seconds
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, // Thicker font
                    color: theme.palette.primary.main, // Brighter primary color
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <CodeIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.main }} />
                System API Documentation Portal
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                A comprehensive, organized reference for core system integration.
            </Typography>

            <Paper elevation={6} sx={{ p: 3, borderRadius: 2 }}>
                
                {/* 1. TAB NAVIGATION (Smoother & Cleaner) */}
                <Tabs 
                    value={currentTab} 
                    onChange={handleChange} 
                    indicatorColor="secondary" 
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ 
                        borderBottom: `2px solid ${theme.palette.divider}`, 
                        mb: 3 
                    }}
                >
                    {apiData.map((group, index) => (
                        <Tab 
                            key={index} 
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                                    <group.icon sx={{ mr: 1 }} />
                                    {group.category}
                                </Box>
                            } 
                        />
                    ))}
                </Tabs>

                {/* 2. TAB CONTENT */}
                <Box sx={{ pt: 1 }}>
                    {apiData.map((group, groupIndex) => (
                        <div
                            key={groupIndex}
                            role="tabpanel"
                            hidden={currentTab !== groupIndex}
                        >
                            {currentTab === groupIndex && (
                                <Box sx={{ p: 0 }}>
                                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: theme.palette.text.primary }}>
                                        {group.category} Endpoints
                                    </Typography>

                                    {/* ACCORDION FOR EACH ENDPOINT (Functional Detail View) */}
                                    {group.endpoints.map((endpoint, endpointIndex) => {
                                        const methodStyle = getMethodColor(endpoint.method);
                                        const responseCode = endpoint.response.status;
                                        const accordionId = `api-endpoint-${groupIndex}-${endpointIndex}`; // 🛠️ FIX: Unique ID
                                        return (
                                            <Accordion 
                                                key={accordionId} // 🛠️ FIX: Use the stable, unique ID as the key
                                                id={accordionId} // 🛠️ FIX: Add a stable ID to the Accordion for internal MUI use
                                                defaultExpanded={false}
                                                sx={{ 
                                                    mb: 1.5, 
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    borderRadius: '8px !important', // Rounded corners for professional look
                                                    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                                                    '&:hover': { backgroundColor: theme.palette.action.hover }
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon color="primary" />}
                                                    sx={{ minHeight: 64, '& .MuiAccordionSummary-content': { margin: '12px 0' } }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Chip 
                                                            label={methodStyle.label} 
                                                            size="medium"
                                                            sx={{ 
                                                                ...methodStyle.sx,
                                                                minWidth: '80px', // Wider chip
                                                                fontWeight: 'bold',
                                                                mr: 3, // More spacing
                                                                fontSize: '0.9rem'
                                                            }}
                                                        />
                                                        <Typography 
                                                            variant="body1" 
                                                            sx={{ 
                                                                flexGrow: 1, 
                                                                fontWeight: 600, 
                                                                color: theme.palette.info.dark, 
                                                                fontFamily: 'monospace' // Path uses monospaced font
                                                            }}
                                                        >
                                                            {endpoint.path}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary" sx={{ ml: 2, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                                                            {endpoint.description}
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                                    <Divider sx={{ my: 2 }} />
                                                    
                                                    {/* Response Code Tag */}
                                                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                                                            Status Code:
                                                        </Typography>
                                                        <Chip 
                                                            label={responseCode} 
                                                            size="small" 
                                                            color={responseCode >= 200 && responseCode < 300 ? 'success' : 'warning'} 
                                                            sx={{ fontWeight: 'bold' }}
                                                        />
                                                    </Box>

                                                    {/* Code Block with Copy Functionality */}
                                                    <Typography variant="h6" sx={{ mt: 2, mb: 1, color: theme.palette.text.secondary }}>
                                                        Example JSON Response
                                                    </Typography>
                                                    <Box 
                                                        sx={{ 
                                                            position: 'relative',
                                                            backgroundColor: '#272822', // Deep Code Editor Background
                                                            color: '#F8F8F2',           // Light Code Text
                                                            p: 2, 
                                                            borderRadius: 1, 
                                                            overflowX: 'auto',
                                                            fontFamily: 'monospace'
                                                        }}
                                                    >
                                                        <Tooltip title={copied ? "Copied!" : "Copy to Clipboard"} placement="top">
                                                            <IconButton
                                                                onClick={() => copyToClipboard(JSON.stringify(endpoint.response, null, 2))}
                                                                size="small"
                                                                sx={{ 
                                                                    position: 'absolute', 
                                                                    top: 8, 
                                                                    right: 8, 
                                                                    color: 'rgba(255, 255, 255, 0.8)',
                                                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' }
                                                                }}
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <pre style={{ margin: 0 }}>
                                                            {JSON.stringify(endpoint.response, null, 2)}
                                                        </pre>
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Box>
                            )}
                        </div>
                    ))}
                </Box>
            </Paper>
        </Container>
    );
};

export default APIDocumentation;